import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { NODE_TYPES, NODE_CATEGORIES, getNodeTypeDef } from '../../data/soarNodes';
import { buildIndex, searchIndex } from './TaskPicker.search';
import { recommend } from '../../utils/insertionHints';
import { track as trackTelemetry } from '../../services/canvasTelemetry';

/**
 * TaskPicker — the inline overlay picker for canvas insertion.
 *
 * Spec: agentlets-production/CANVAS_PICKER_INLINE_DESIGN.md
 *
 * v2 redesign (May 2026) — what changed
 * --------------------------------------
 *   • Sized for impact: 640 × 600 max (was 340 × 480). When this picker
 *     is open, CanvasPageV3 auto-collapses both side panes so it can take
 *     the full canvas stage without competing with the chrome.
 *   • Tab strip is a prominent flex row, no overflow scroll. Active tab
 *     uses the brand red, not charcoal. 7 tabs share the row evenly.
 *   • No more vertical scrolling. The current tab's items are paginated
 *     in pages of 5; ←/→ arrows and a "1 of N" indicator below the list
 *     navigate between pages. Search results paginate the same way.
 *   • Keyboard:
 *       Tab / Shift-Tab          cycle tabs
 *       ↑ / ↓                    move selection within the visible page
 *       ← / →                    previous / next page (when search empty)
 *       Enter                    insert highlighted row
 *       /                        focus search
 *       Esc                      close
 *     The global ⌘E summon (and the bottom-toolbar "+" button that maps to
 *     it) is wired in CanvasPageV3, not here.
 *
 * Props are unchanged from v1; the parent doesn't need to be updated.
 */
const PICKER_WIDTH = 640;
const PICKER_MAX_HEIGHT = 600;
const PAGE_SIZE = 5;

const ACCENT = 'var(--v2-action)';      // brand red
const ACCENT_BG = 'var(--v2-action-soft)';   // brand-tinted hover / selected
const BG = 'var(--v2-bg-surface)';
const FG = 'var(--v2-text-primary)';
const MUTED = 'var(--v2-text-muted)';
const HAIR = 'var(--v2-border-default)';

export default function TaskPicker({
  isOpen,
  target,
  anchor,
  nodes,
  connections,
  installedConnectors,
  marketplaceConnectors,
  agents,
  recipes,
  onChoose,
  onClose,
}) {
  const [activeTab, setActiveTab] = useState('for-you');
  const [searchValue, setSearchValue] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [expandedConnectorId, setExpandedConnectorId] = useState(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Open-once initialiser — same pattern as v1, ref-gated so a parent
  // re-render with a fresh `target` reference doesn't reset state mid-edit.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      if (wasOpenRef.current) return undefined;
      wasOpenRef.current = true;
      setActiveTab('for-you');
      setSearchValue('');
      setSelectedId(null);
      setPageIndex(0);
      setExpandedConnectorId(null);
      const handle = requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
      trackTelemetry('picker_open', { targetKind: target?.kind || 'unknown' });
      return () => cancelAnimationFrame(handle);
    }
    wasOpenRef.current = false;
    return undefined;
  }, [isOpen, target]);

  // Reset page when the user switches tabs, types, or expands a connector;
  // anything that changes the visible item set should land on page 0.
  useEffect(() => { setPageIndex(0); }, [activeTab, searchValue, expandedConnectorId]);

  // Recommendation hints — Phase 4 rules, surfaced on the For-you tab.
  const hintEntries = useMemo(() => {
    if (!target) return [];
    const hints = recommend(target, { nodes: nodes || [], connections: connections || [] }, 4);
    return hints.map((h) => {
      const def = getNodeTypeDef(h.type);
      return {
        kind: 'type',
        id: `hint:${h.type}`,
        label: def?.label || h.type,
        description: h.reason,
        type: h.type,
        tab: 'for-you',
        score: h.score,
        reason: h.reason,
        isHint: true,
      };
    });
  }, [target, nodes, connections]);

  // Build the full searchable index from the platform's catalogues. Cheap
  // (~1 ms / 300 entries) so we recompute when source data changes.
  const index = useMemo(() => buildIndex({
    nodeTypes: NODE_TYPES,
    nodeCategories: NODE_CATEGORIES,
    installedConnectors: installedConnectors ? Object.values(installedConnectors) : [],
    marketplaceConnectors: marketplaceConnectors || [],
    agents: agents || [],
    recipes: recipes || [],
  }), [installedConnectors, marketplaceConnectors, agents, recipes]);

  // visibleEntries is the item set for the CURRENT tab + search. The list
  // gets paginated below; this is the full list before pagination.
  const visibleEntries = useMemo(() => {
    const q = searchValue.trim();
    if (q) {
      const all = searchIndex(index, q, 100);
      if (activeTab === 'for-you') return all;
      return all.filter((e) => e.tab === activeTab);
    }
    if (activeTab === 'for-you') {
      const recipeEntries = index.filter((e) => e.kind === 'recipe');
      const quickpicks = forYouDefault(index).filter((e) => e.kind === 'type');
      const seen = new Set();
      const out = [];
      for (const list of [hintEntries, recipeEntries, quickpicks]) {
        for (const e of list) {
          if (seen.has(e.id)) continue;
          seen.add(e.id);
          out.push(e);
        }
      }
      return out;
    }
    return index.filter((e) => e.tab === activeTab);
  }, [index, searchValue, activeTab, hintEntries]);

  // Pagination math.
  const totalPages = Math.max(1, Math.ceil(visibleEntries.length / PAGE_SIZE));
  const safePage = Math.min(pageIndex, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageEntries = visibleEntries.slice(pageStart, pageStart + PAGE_SIZE);

  // Keep selection inside the current page; if it falls off, snap to the
  // first row of the visible page.
  useEffect(() => {
    if (pageEntries.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !pageEntries.find((e) => e.id === selectedId)) {
      setSelectedId(pageEntries[0].id);
    }
  }, [pageEntries, selectedId]);

  const goToPage = useCallback((delta) => {
    setPageIndex((p) => Math.max(0, Math.min(totalPages - 1, p + delta)));
  }, [totalPages]);

  const moveSelection = useCallback((delta) => {
    if (pageEntries.length === 0) return;
    const i = pageEntries.findIndex((e) => e.id === selectedId);
    const next = (i + delta + pageEntries.length) % pageEntries.length;
    setSelectedId(pageEntries[next].id);
  }, [pageEntries, selectedId]);

  const handleChooseEntry = useCallback((entry) => {
    if (!entry || !onChoose) return;
    trackTelemetry(entry.isHint ? 'hint_followed' : 'row_chosen', {
      kind: entry.kind,
      type: entry.type || null,
      recipeId: entry.recipeId || null,
      tab: entry.tab || null,
    });
    if (entry.kind === 'type') {
      onChoose({ kind: 'type', type: entry.type, label: entry.label });
    } else if (entry.kind === 'recipe') {
      trackTelemetry('recipe_used', { recipeId: entry.recipeId });
      onChoose({ kind: 'recipe', recipeId: entry.recipeId, label: entry.label });
    } else if (entry.kind === 'connector-skill') {
      onChoose({
        kind: 'type',
        type: 'connector_task',
        label: entry.label,
        config: {
          connectorId: entry.connectorId,
          connector: entry.connectorSlug,
          action: entry.skillId,
        },
      });
    } else if (entry.kind === 'agent') {
      onChoose({
        kind: 'type',
        type: entry.isConversational ? 'agent_chat' : 'agent_task',
        label: entry.label,
        config: { agentId: entry.agentId },
      });
    } else if (entry.kind === 'connector') {
      setExpandedConnectorId((prev) => (prev === entry.id ? null : entry.id));
    }
  }, [onChoose]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); moveSelection(1); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); moveSelection(-1); return; }
    if (!searchValue && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      goToPage(e.key === 'ArrowRight' ? 1 : -1);
      return;
    }
    if (searchValue && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      // While searching, ←/→ are cursor keys in the input. Don't hijack.
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const i = TABS.findIndex((t) => t.id === activeTab);
      const next = (i + (e.shiftKey ? -1 : 1) + TABS.length) % TABS.length;
      setActiveTab(TABS[next].id);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const entry = pageEntries.find((x) => x.id === selectedId);
      if (entry) handleChooseEntry(entry);
      return;
    }
    if (e.key === '/') {
      e.preventDefault();
      searchInputRef.current?.focus();
    }
  }, [isOpen, onClose, moveSelection, goToPage, searchValue, activeTab, pageEntries, selectedId, handleChooseEntry]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocClick = (e) => {
      const root = containerRef.current;
      if (root && !root.contains(e.target)) onClose?.();
    };
    document.addEventListener('mousedown', onDocClick, true);
    return () => document.removeEventListener('mousedown', onDocClick, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // RC-20d (task #1) — Centred modal at click site. The picker now ALWAYS
  // renders as a fixed-position centred modal with a dimmed backdrop, like
  // VS Code's command palette / Linear's quick-add / Notion's slash menu.
  //
  // Why drop the anchor-positioning: when the picker tried to position
  // itself at the click coordinates (e.g. a "+" button near the right
  // edge of the canvas), the inline clamping math frequently produced a
  // picker that lived in the lower-right quadrant with its tabs running
  // off the right edge of the viewport. The fix at the math layer would
  // have been incremental (tighter clamp + better anchor anchoring), but
  // it doesn't address the deeper UX problem: an anchored popover near
  // the canvas edge competes visually with the rest of the canvas, which
  // is exactly what the operator was reading as "cuts off".
  //
  // A centred fixed modal sidesteps the entire class of clipping bugs,
  // matches every prevailing command-palette idiom, and reads as the
  // single focused decision the user is making. The `anchor` prop is
  // intentionally ignored; legacy callers passing one don't error, they
  // just get the same centred render as everyone else.
  const prefersReducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Backdrop click closes — same semantic as VS Code's command palette.
  // Stop propagation on the inner modal so backdrop clicks only fire
  // when the user actually clicks outside the picker chrome.
  const handleBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      onMouseDown={handleBackdropMouseDown}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.32)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 'min(15vh, 120px)',
        animation: prefersReducedMotion ? 'none' : 'taskPickerBackdrop 140ms ease-out',
      }}
    >
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label="Insert task"
      onKeyDown={handleKeyDown}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'relative',
        background: BG,
        border: `0.5px solid rgba(0,0,0,0.16)`,
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.22), 0 4px 12px rgba(0,0,0,0.06)',
        width: PICKER_WIDTH,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'min(70vh, ' + PICKER_MAX_HEIGHT + 'px)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
        animation: prefersReducedMotion ? 'none' : 'taskPickerOpen 140ms cubic-bezier(0.16, 1, 0.3, 1)',
        transformOrigin: 'top center',
      }}
    >
      <TabStrip
        activeTab={activeTab}
        onTabChange={(t) => { setActiveTab(t); setSelectedId(null); }}
      />

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 18px',
        borderBottom: `0.5px solid ${HAIR}`,
      }}>
        <SearchGlyph />
        <input
          ref={searchInputRef}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search or describe what you need…"
          aria-label="Search tasks"
          style={{
            flex: 1, minWidth: 0,
            border: 'none', outline: 'none',
            background: 'transparent',
            fontSize: 14, color: FG,
          }}
        />
        <KbdHint>/</KbdHint>
      </div>

      <ContextBarFromTarget target={target} nodes={nodes} connections={connections} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {visibleEntries.length === 0 ? (
          <EmptyState query={searchValue} onClearSearch={() => setSearchValue('')} />
        ) : (
          <>
            <div style={{ flex: 1, overflow: 'hidden', padding: '8px 0 4px' }}>
              <PickerBody
                entries={pageEntries}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onChoose={handleChooseEntry}
                expandedConnectorId={expandedConnectorId}
                onToggleConnector={(id) => setExpandedConnectorId((p) => (p === id ? null : id))}
              />
            </div>

            {totalPages > 1 && (
              <Pagination
                page={safePage}
                totalPages={totalPages}
                totalEntries={visibleEntries.length}
                onPrev={() => goToPage(-1)}
                onNext={() => goToPage(1)}
              />
            )}
          </>
        )}
      </div>

      <Footer onClose={onClose} />

      <style>{`
        @keyframes taskPickerOpen {
          from { opacity: 0; transform: scale(0.94) translateY(4px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        /* RC-20d — backdrop fade-in matches the picker pop-in tempo so
           the modal reads as one composed appearance. */
        @keyframes taskPickerBackdrop {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
    </div>
  );
}

// ============================================================================
// Tabs — prominent, no overflow scroll, brand-coloured active.
// ============================================================================

const TABS = [
  { id: 'for-you',   label: 'For you',  hint: '★' },
  { id: 'trigger',   label: 'Trigger' },
  { id: 'worker',    label: 'Worker' },
  { id: 'operator',  label: 'Operator' },
  { id: 'ai',        label: 'AI' },
  { id: 'connector', label: 'Connector' },
  { id: 'agent',     label: 'Agent' },
];

function TabStrip({ activeTab, onTabChange }) {
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        padding: '14px 14px 0',
        borderBottom: `0.5px solid ${HAIR}`,
        gap: 4,
      }}
    >
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(tab.id)}
            style={{
              flex: 1,
              border: 'none',
              cursor: 'pointer',
              padding: '8px 10px 10px',
              borderRadius: 0,
              fontSize: 13,
              fontWeight: isActive ? 500 : 400,
              background: 'transparent',
              color: isActive ? ACCENT : MUTED,
              borderBottom: isActive ? `2px solid ${ACCENT}` : '2px solid transparent',
              marginBottom: -1,
              transition: 'color 120ms ease, border-color 120ms ease',
              whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            }}
          >
            {tab.id === 'for-you' && <Sparkles size={11} style={{ color: isActive ? ACCENT : 'var(--v2-text-disabled)' }} />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Body, rows
// ============================================================================

function PickerBody({
  entries, selectedId, onSelect, onChoose,
  expandedConnectorId, onToggleConnector,
}) {
  return (
    <div>
      {entries.map((entry) => (
        <PickerRow
          key={entry.id}
          entry={entry}
          selected={entry.id === selectedId}
          expanded={entry.kind === 'connector' && entry.id === expandedConnectorId}
          onMouseEnter={() => onSelect(entry.id)}
          onClick={() => {
            if (entry.kind === 'connector') { onToggleConnector(entry.id); return; }
            onChoose(entry);
          }}
          onChooseSkill={(skill) => {
            onChoose({
              kind: 'connector-skill',
              connectorId: entry.connectorId,
              connectorSlug: entry.connectorSlug,
              skillId: skill.id,
              label: skill.name || skill.id,
              tab: 'connector',
            });
          }}
        />
      ))}
    </div>
  );
}

function PickerRow({ entry, selected, expanded, onMouseEnter, onClick, onChooseSkill }) {
  if (entry.kind === 'connector') {
    return (
      <div data-row-id={entry.id} style={{ borderBottom: expanded ? `0.5px solid ${HAIR}` : 'none' }}>
        <button
          onMouseEnter={onMouseEnter}
          onClick={onClick}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            width: '100%',
            padding: '10px 18px',
            border: 'none',
            background: selected ? ACCENT_BG : 'transparent',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <IconTile entry={entry} />
          <span style={{ fontSize: 13, fontWeight: 500, color: entry.notInstalled ? 'var(--v2-text-disabled)' : FG }}>
            {entry.label}
          </span>
          <span style={{
            fontSize: 12, color: MUTED,
            flex: 1, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {entry.description}
          </span>
          {entry.notInstalled && <Badge tone="warn">not installed</Badge>}
          <ChevronRight
            size={15}
            style={{
              color: 'var(--v2-text-disabled)',
              transform: expanded ? 'rotate(90deg)' : 'none',
              transition: 'transform 120ms ease',
            }}
          />
        </button>
        {expanded && (entry.skills || []).length > 0 && (
          <div style={{ background: 'var(--v2-bg-elevated)', paddingLeft: 18 }}>
            {entry.skills.map((skill) => (
              <button
                key={skill.id}
                onClick={() => onChooseSkill(skill)}
                disabled={entry.notInstalled}
                title={entry.notInstalled ? 'Install this connector first' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 18px',
                  border: 'none', background: 'transparent',
                  cursor: entry.notInstalled ? 'not-allowed' : 'pointer',
                  opacity: entry.notInstalled ? 0.5 : 1,
                  textAlign: 'left',
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--v2-border-default)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: FG, fontWeight: 500 }}>{skill.name || skill.id}</span>
                <span style={{
                  fontSize: 12, color: MUTED,
                  flex: 1, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontFamily: skill.method ? 'SFMono-Regular, Menlo, monospace' : 'inherit',
                }}>
                  {skill.method && skill.endpoint ? `${skill.method} ${skill.endpoint}` : (skill.description || '')}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      data-row-id={entry.id}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      title={entry.isHint ? entry.reason : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        width: '100%',
        padding: '10px 18px',
        border: 'none',
        background: selected ? ACCENT_BG : 'transparent',
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      {entry.isHint
        ? <span style={{
            width: 26, height: 26, borderRadius: 7,
            background: 'var(--v2-warning-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Sparkles size={13} style={{ color: 'var(--v2-warning)' }} />
          </span>
        : <IconTile entry={entry} />}
      <span style={{ fontSize: 13, fontWeight: 500, color: FG, flexShrink: 0 }}>{entry.label}</span>
      <span style={{
        fontSize: 12, color: MUTED,
        flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {entry.description}
      </span>
      {entry.kind === 'recipe' && entry.nodeCount && (
        <Badge tone="info">{entry.nodeCount} nodes</Badge>
      )}
      {entry.isHint && <Badge tone="info">why?</Badge>}
    </button>
  );
}

function IconTile({ entry }) {
  const palette = {
    type: 'var(--v2-action)',
    recipe: 'var(--v2-success)',
    connector: 'var(--v2-accent-rose)',
    'connector-skill': 'var(--v2-accent-rose)',
    agent: 'var(--v2-accent-trust)',
  };
  const tint = entry.color || palette[entry.kind] || 'var(--v2-text-muted)';
  return (
    <span style={{
      width: 26, height: 26,
      borderRadius: 7,
      background: tint + '22',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: tint }} />
    </span>
  );
}

function Badge({ children, tone = 'neutral' }) {
  const palette = {
    neutral: { bg: 'var(--v2-bg-muted)', fg: 'var(--v2-text-secondary)' },
    info:    { bg: 'var(--v2-danger-soft)', fg: 'var(--v2-danger)' },
    warn:    { bg: 'var(--v2-warning-soft)', fg: 'var(--v2-warning)' },
  };
  const p = palette[tone] || palette.neutral;
  return (
    <span style={{
      fontSize: 11, padding: '2px 7px', borderRadius: 4,
      background: p.bg, color: p.fg, fontWeight: 500, flexShrink: 0,
    }}>{children}</span>
  );
}

// ============================================================================
// Pagination
// ============================================================================

function Pagination({ page, totalPages, totalEntries, onPrev, onNext }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 18px',
      borderTop: `0.5px solid ${HAIR}`,
      background: 'var(--v2-bg-elevated)',
    }}>
      <span style={{ fontSize: 11, color: MUTED }}>
        {totalEntries} item{totalEntries === 1 ? '' : 's'}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={onPrev}
          disabled={page === 0}
          aria-label="Previous page"
          style={{
            border: `0.5px solid ${HAIR}`,
            background: BG, color: page === 0 ? 'var(--v2-border-default)' : FG,
            width: 28, height: 28, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: page === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <ChevronLeft size={14} />
        </button>
        <span style={{ fontSize: 12, color: FG, fontWeight: 500, minWidth: 50, textAlign: 'center' }}>
          {page + 1} / {totalPages}
        </span>
        <button
          onClick={onNext}
          disabled={page >= totalPages - 1}
          aria-label="Next page"
          style={{
            border: `0.5px solid ${HAIR}`,
            background: BG, color: page >= totalPages - 1 ? 'var(--v2-border-default)' : FG,
            width: 28, height: 28, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
          }}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Header context bar + footer + helpers
// ============================================================================

function ContextBarFromTarget({ target, nodes, connections }) {
  const info = useMemo(() => {
    if (!target) return null;
    const findNode = (id) => nodes?.find((n) => n.id === id) || null;
    const findConn = (id) => connections?.find((c) => c.id === id) || null;
    if (target.kind === 'edge') {
      const conn = findConn(target.connectionId);
      if (!conn) return null;
      const from = findNode(conn.from);
      const to = findNode(conn.to);
      if (!from || !to) return null;
      return { mode: 'edge', from: from.label || from.type, to: to.label || to.type };
    }
    if (target.kind === 'branch') {
      const sw = findNode(target.switchNodeId);
      return {
        mode: 'branch',
        switchLabel: sw?.label || sw?.type || 'switch',
        branchLabel: target.branchLabel || 'branch',
      };
    }
    if (target.kind === 'after-node') {
      const n = findNode(target.nodeId);
      if (!n) return null;
      return { mode: 'after', label: n.label || n.type };
    }
    return null;
  }, [target, nodes, connections]);

  if (!info) return null;
  const chip = {
    fontSize: 12, padding: '2px 8px',
    background: 'var(--v2-bg-muted)', color: FG,
    borderRadius: 5, fontWeight: 500,
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      padding: '10px 18px',
      borderBottom: `0.5px solid ${HAIR}`,
      fontSize: 12, color: MUTED,
    }}>
      {info.mode === 'edge' && (<>
        <span>Inserting between</span>
        <span style={chip}>{info.from}</span>
        <span style={{ color: 'var(--v2-text-disabled)' }}>→</span>
        <span style={chip}>{info.to}</span>
      </>)}
      {info.mode === 'branch' && (<>
        <span>Inserting on the</span>
        <span style={chip}>{info.branchLabel}</span>
        <span>branch of</span>
        <span style={chip}>{info.switchLabel}</span>
      </>)}
      {info.mode === 'after' && (<>
        <span>Adding after</span>
        <span style={chip}>{info.label}</span>
      </>)}
    </div>
  );
}

function Footer({ onClose }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '10px 18px',
      borderTop: `0.5px solid ${HAIR}`,
      fontSize: 12, color: MUTED,
      flexShrink: 0,
    }}>
      <KbdHint>↑↓</KbdHint><span>nav</span>
      <KbdHint>←→</KbdHint><span>page</span>
      <KbdHint>Tab</KbdHint><span>cycle tabs</span>
      <KbdHint>↵</KbdHint><span>insert</span>
      <KbdHint>⌘E</KbdHint><span>open anywhere</span>
      <span style={{ flex: 1 }} />
      <button
        onClick={onClose}
        aria-label="Close picker"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--v2-text-disabled)', padding: 2, display: 'flex' }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

function EmptyState({ query, onClearSearch }) {
  return (
    <div style={{ padding: '32px 24px', fontSize: 13, color: MUTED, lineHeight: 1.6, textAlign: 'center' }}>
      {query
        ? <>No matches for <strong style={{ color: FG }}>“{query}”</strong>. <button onClick={onClearSearch} style={{ color: 'var(--v2-action)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13 }}>Clear search</button> or pick a category.</>
        : <>Nothing in this tab yet.</>}
    </div>
  );
}

function KbdHint({ children }) {
  return (
    <span style={{
      fontFamily: 'SFMono-Regular, Menlo, monospace',
      fontSize: 11, padding: '2px 6px',
      background: 'var(--v2-bg-muted)',
      border: `0.5px solid ${HAIR}`,
      borderRadius: 4, color: 'var(--v2-text-secondary)',
    }}>{children}</span>
  );
}

function SearchGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="#a8a29e" strokeWidth="1.5" aria-hidden="true">
      <circle cx="5" cy="5" r="3.5" />
      <path d="M8 8l3 3" strokeLinecap="round" />
    </svg>
  );
}

function forYouDefault(index) {
  const QUICKPICKS = new Set([
    'http_task', 'llm_task', 'script_task', 'condition',
    'approval_gate', 'human_task',
    'notification_task', 'connector_task', 'terminate',
  ]);
  const triggers = index.filter((e) => e.tab === 'trigger' && e.kind === 'type').slice(0, 4);
  const quickpicks = index.filter((e) => e.kind === 'type' && QUICKPICKS.has(e.type));
  const recipes = index.filter((e) => e.kind === 'recipe');
  const seen = new Set();
  const all = [...triggers, ...quickpicks, ...recipes];
  return all.filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
}
